import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { theme } from './styles/theme';

import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider as AppThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import ProtectedRoute from './components/auth/ProtectedRoute';

import DashboardLayout from './layouts/Dashboard/DashboardLayout';
import AuthLayout from './layouts/Auth/AuthLayout';

import LoginPage from './pages/Auth/LoginPage';
import ForbiddenPage from './pages/Auth/ForbiddenPage';
import NotFoundPage from './pages/NotFoundPage';

import DashboardPage from './pages/Dashboard/DashboardPage';
import FeesPage from './pages/Fees/FeesPage';
import ChartOfAccountsDashboard from './pages/Ledger/ChartOfAccountsDashboard';
import ChartOfAccountsPage from './pages/Ledger/ChartOfAccountsPage';
import ChartOfAccountsTreeView from './pages/Ledger/ChartOfAccountsTreeView';
import ChartOfAccountsCardPage from './pages/Ledger/ChartOfAccountsCardPage';
import ModernChartOfAccounts from './pages/ModernChartOfAccounts';

import VouchersListPage from './pages/Vouchers/VouchersListPage';
import VoucherFormPage from './pages/Vouchers/VoucherFormPage';

import ReportsPage from './pages/Reports/ReportsPage';
import TrialBalancePage from './pages/Reports/TrialBalancePage';
import YtdReportPage from './pages/Reports/YtdReportPage';
import PnlPage from './pages/Reports/PnlPage';
import BalanceSheetPage from './pages/Reports/BalanceSheetPage';
import OutstandingVouchersPage from './pages/Reports/OutstandingVouchersPage';
import AuditLogPage from './pages/Reports/AuditLogPage';

import UsersPage      from './pages/Admin/UsersPage';
import RolesPage      from './pages/Admin/RolesPage';
import EntitiesPage   from './pages/Admin/EntitiesPage';
import CurrenciesPage from './pages/Admin/CurrenciesPage';
import PeriodsPage    from './pages/Admin/PeriodsPage';

import DailyClosingPage  from './pages/CashBanks/DailyClosingPage';
import DailyReportPage   from './pages/CashBanks/DailyReportPage';
import ChequesPage       from './pages/CashBanks/ChequesPage';
import ChequesPrintPage  from './pages/CashBanks/ChequesPrintPage';
import VendorsPage              from './pages/Vendors/VendorsPage';
import PurchaseRequestsPage    from './pages/Purchasing/PurchaseRequestsPage';
import PurchaseOrdersPage      from './pages/Purchasing/PurchaseOrdersPage';
import PurchaseOrderFormPage   from './pages/Purchasing/PurchaseOrderFormPage';
import PurchaseInvoicesPage    from './pages/Purchasing/PurchaseInvoicesPage';
import PurchaseInvoiceFormPage from './pages/Purchasing/PurchaseInvoiceFormPage';

import './styles/global.css';

const COA_VIEW        = 'accounts.view';
const VOUCHERS_VIEW   = 'vouchers.view';
const VOUCHERS_CREATE = 'vouchers.create';
const REPORTS_VIEW    = 'reports.view';
const CB_VIEW         = 'cash_banks.view';
const CHEQUES_VIEW    = 'cheques.view';
const VENDORS_VIEW    = 'vendors.view';
const PR_VIEW         = 'purchases.pr.view';
const PO_VIEW         = 'purchases.po.view';
const PO_CREATE       = 'purchases.po.create';
const PI_VIEW         = 'purchases.invoice.view';
const PI_CREATE       = 'purchases.invoice.create';

function App() {
  return (
    <ThemeProvider theme={theme}>
      <AppThemeProvider>
      <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App">
            <Routes>
              {/* Public routes */}
              <Route
                path="/login"
                element={
                  <AuthLayout>
                    <LoginPage />
                  </AuthLayout>
                }
              />

              {/* Protected app routes */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Routes>
                        <Route index element={<DashboardPage />} />
                        <Route path="dashboard" element={<DashboardPage />} />
                        <Route path="fees" element={<FeesPage />} />

                        <Route
                          path="chart-of-accounts"
                          element={
                            <ProtectedRoute permission={COA_VIEW}>
                              <ChartOfAccountsDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="chart-of-accounts/table"
                          element={
                            <ProtectedRoute permission={COA_VIEW}>
                              <ChartOfAccountsPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="chart-of-accounts/card"
                          element={
                            <ProtectedRoute permission={COA_VIEW}>
                              <ChartOfAccountsCardPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="chart-of-accounts/tree"
                          element={
                            <ProtectedRoute permission={COA_VIEW}>
                              <ChartOfAccountsTreeView />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="chart-of-accounts/modern"
                          element={
                            <ProtectedRoute permission={COA_VIEW}>
                              <ModernChartOfAccounts />
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="vouchers"
                          element={
                            <ProtectedRoute permission={VOUCHERS_VIEW}>
                              <VouchersListPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="vouchers/new"
                          element={
                            <ProtectedRoute permission={VOUCHERS_CREATE}>
                              <VoucherFormPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="vouchers/:id"
                          element={
                            <ProtectedRoute permission={VOUCHERS_VIEW}>
                              <VoucherFormPage />
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="reports"
                          element={
                            <ProtectedRoute permission={REPORTS_VIEW}>
                              <ReportsPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="reports/trial-balance"
                          element={
                            <ProtectedRoute permission={REPORTS_VIEW}>
                              <TrialBalancePage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="reports/ytd"
                          element={
                            <ProtectedRoute permission={REPORTS_VIEW}>
                              <YtdReportPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="reports/pnl"
                          element={
                            <ProtectedRoute permission={REPORTS_VIEW}>
                              <PnlPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="reports/balance-sheet"
                          element={
                            <ProtectedRoute permission={REPORTS_VIEW}>
                              <BalanceSheetPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="reports/outstanding-vouchers"
                          element={
                            <ProtectedRoute permission={REPORTS_VIEW}>
                              <OutstandingVouchersPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="reports/audit-log"
                          element={
                            <ProtectedRoute permission={REPORTS_VIEW}>
                              <AuditLogPage />
                            </ProtectedRoute>
                          }
                        />

                        {/* Vendors — Phase 6a */}
                        <Route
                          path="vendors"
                          element={
                            <ProtectedRoute permission={VENDORS_VIEW}>
                              <VendorsPage />
                            </ProtectedRoute>
                          }
                        />

                        {/* Purchasing — Phase 6b */}
                        <Route
                          path="purchasing/requests"
                          element={
                            <ProtectedRoute permission={PR_VIEW}>
                              <PurchaseRequestsPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="purchasing/orders"
                          element={
                            <ProtectedRoute permission={PO_VIEW}>
                              <PurchaseOrdersPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="purchasing/orders/new"
                          element={
                            <ProtectedRoute permission={PO_CREATE}>
                              <PurchaseOrderFormPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="purchasing/orders/:id/edit"
                          element={
                            <ProtectedRoute permission={PO_CREATE}>
                              <PurchaseOrderFormPage />
                            </ProtectedRoute>
                          }
                        />

                        {/* Purchasing — Phase 6c */}
                        <Route
                          path="purchasing/invoices"
                          element={
                            <ProtectedRoute permission={PI_VIEW}>
                              <PurchaseInvoicesPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="purchasing/invoices/new"
                          element={
                            <ProtectedRoute permission={PI_CREATE}>
                              <PurchaseInvoiceFormPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="purchasing/invoices/:id/edit"
                          element={
                            <ProtectedRoute permission={PI_CREATE}>
                              <PurchaseInvoiceFormPage />
                            </ProtectedRoute>
                          }
                        />

                        {/* /admin/audit was merged into /reports/audit-log (Phase 4c decision) */}
                        <Route path="admin/audit" element={<Navigate to="/reports/audit-log" replace />} />

                        {/* Admin pages */}
                        <Route
                          path="admin/users"
                          element={
                            <ProtectedRoute permission="users.view">
                              <UsersPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="admin/roles"
                          element={
                            <ProtectedRoute permission="roles.view">
                              <RolesPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="admin/entities"
                          element={
                            <ProtectedRoute permission="entities.view">
                              <EntitiesPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="admin/currencies"
                          element={
                            <ProtectedRoute permission="currencies.view">
                              <CurrenciesPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="admin/periods"
                          element={
                            <ProtectedRoute permission="periods.view">
                              <PeriodsPage />
                            </ProtectedRoute>
                          }
                        />

                        {/* Cash & Banks — Phase 5 */}
                        <Route
                          path="cash-banks/closing"
                          element={
                            <ProtectedRoute permission={CB_VIEW}>
                              <DailyClosingPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="cash-banks/daily-report"
                          element={
                            <ProtectedRoute permission={CB_VIEW}>
                              <DailyReportPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="cash-banks/cheques"
                          element={
                            <ProtectedRoute permission={CHEQUES_VIEW}>
                              <ChequesPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="cash-banks/cheques/print/:id"
                          element={
                            <ProtectedRoute permission={CHEQUES_VIEW}>
                              <ChequesPrintPage />
                            </ProtectedRoute>
                          }
                        />

                        <Route path="forbidden" element={<ForbiddenPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </div>
        </BrowserRouter>
      </AuthProvider>
      </LanguageProvider>
      </AppThemeProvider>
    </ThemeProvider>
  );
}

export default App;
